import type { NextSteps } from "../../../server/modules/homecare/careplans";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { chip, ib, table } from "../../ui/record";
import { openSend } from "../../ui/send";
import { openViewer } from "../../ui/viewer";
import { area, field, refuse, select } from "../clients/forms";
import { filePick, form, go } from "./careplan";
import { filedHere } from "./fileit";
import { openAppointmentForm } from "./appointment-form";

// The Next steps card on Patient and payer, under Authorizations (brief 1C-4, mockup
// nextStepsCard): the episode's physician orders (Order, Sent, Signed; take or file one, send it
// to the physician through the Send sheet, co-sign an LPN's, file the signed copy), Due next
// (reassessment, recertification, each unsigned order with its days left on the clock,
// authorizations ending) and the Next appointments. Shown when the client type's Physician orders
// setting is Required or Optional; hidden when Not used. Small actions are icon buttons with
// hover tips. Each column keeps min-width 0, so on a phone the orders table scrolls inside its
// frame instead of widening the card.

type Data = NextSteps & { error?: string; moved?: string | null };
type Res = { ok: boolean; status: number; body: Data };
type Order = Data["orders"][number];

/** Draws the Next steps card into `host`. Answers a reload. */
export async function nextStepsCard(
  host: HTMLElement,
  patientId: string,
  changed: () => void,
): Promise<{ reload: () => Promise<void> }> {
  const base = `/api/patients/${encodeURIComponent(patientId)}`;
  let data: Data | null = null;

  async function reload() {
    const res = (await api<Data>("GET", `${base}/next-steps`)) as Res;
    if (!res.ok) {
      host.replaceChildren();
      return;
    }
    data = res.body;
    draw();
  }
  async function took(res: Res, done?: string, sub?: string): Promise<Res> {
    if (res.ok) {
      data = res.body;
      draw();
      if (done)
        toast(
          done,
          sub ?? (res.body.moved ? `The episode moved on to ${res.body.moved}` : undefined),
        );
      changed();
    } else toast("That wasn't saved", res.body.error);
    return res;
  }
  const send = async (method: string, path: string, body?: unknown, done?: string, sub?: string) =>
    took((await api<Data>(method, `${base}${path}`, body)) as Res, done, sub);

  // ---- Modals -------------------------------------------------------------------------------------

  /** Add physician order: a verbal one taken now, or a written one filed with its page. */
  function orderModal(d: Data) {
    const kinds = [
      ...(d.canTakeVerbal ? [["verbal", "Verbal, taken now"] as const] : []),
      ["written", "Written, filed with its page"] as const,
    ];
    const kind = select("Kind", kinds, kinds[0]?.[0] ?? "written");
    const from = field("From (the physician)", "", { maxlength: "160" });
    const what = area("What the physician ordered", "");
    const file = filePick("The written order (the file)", d.files, patientId, "Upload the order");
    const note = el(
      "p",
      { class: "small muted", style: "margin:0" },
      `Verbal orders: ${d.settings.verbalOrders}.${d.settings.lpn ? " An LPN's order is flagged until an RN co-signs it." : ""} Each one goes to the physician to sign within ${d.settings.orderDays} days.`,
    );
    const shape = () => {
      file.el.hidden = kind.input.value !== "written";
    };
    kind.input.addEventListener("change", shape);
    shape();
    const save = go("Add order", async () => {
      const k = kind.input.value;
      const res = (await api<Data>("POST", `${base}/orders`, {
        kind: k,
        what: what.input.value,
        fromWhom: from.input.value,
        fileId: k === "written" ? file.input.value || undefined : undefined,
      })) as Res;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      const o = res.body.orders[0];
      await took(
        res,
        k === "verbal" ? "Verbal order taken" : "Written order filed",
        o?.cosign
          ? `Flagged until an RN co-signs it by ${o.cosign.due}`
          : "Send it to the physician to sign",
      );
    });
    const m = openModal({
      title: "Add a physician order",
      sub: d.patient.name,
      body: [form(note, kind.el, from.el, what.el, file.el)],
      actions: [save],
    });
  }

  function signedModal(d: Data, orderId: string) {
    const file = filePick("The signed copy", d.files, patientId, "Upload the signed copy");
    const on = field("Signed on", d.today, { type: "date" });
    const save = go("File it", async () => {
      const res = (await api<Data>("POST", `${base}/orders/${orderId}/signed`, {
        fileId: file.input.value,
        signedOn: on.input.value,
      })) as Res;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      await took(res, "Signed copy filed", res.body.moved ? undefined : "The 30-day clock stopped");
    });
    const m = openModal({
      title: "File the signed copy",
      sub: "Back from the physician, signed.",
      body: [form(file.el, on.el)],
      actions: [save],
    });
  }

  /**
   * Add appointment: the one appointment form, the same as the Assessment tab's (brief 1E-28;
   * Thomas, PR #59).
   */
  function appointmentModal() {
    void openAppointmentForm(patientId, async (res) => {
      toast("Appointment saved", res.body.note ?? undefined);
      await reload();
      changed();
    });
  }

  // ---- The card -----------------------------------------------------------------------------------

  const sentWords = (o: Order) => (o.sent ? `Sent ${o.sent.at.split(" ")[0] ?? ""}` : "Not sent");
  const signedChip = (d: Data, o: Order) => {
    if (o.signed) return chip(`Signed ${o.signed.on}`, "ok");
    if (o.cosign?.state === "waiting" || o.cosign?.state === "late")
      return chip(o.status.words, o.status.level);
    if (o.clock.state === "overdue")
      return chip(`Unsigned after ${d.settings.orderDays} days`, "bad");
    const day = Math.min(
      d.settings.orderDays,
      Math.max(1, d.settings.orderDays - o.clock.daysLeft),
    );
    return chip(`Waiting · day ${day} of ${d.settings.orderDays}`, "warn");
  };

  function ordersTable(d: Data): HTMLElement {
    const sendIt = async (o: Order) => {
      const res = await api<{ file: { id: string; name: string } }>(
        "POST",
        `${base}/orders/${o.id}/pdf`,
        {},
      );
      if (!res.ok) return toast("The order wasn't made", (res.body as { error?: string }).error);
      void openSend({
        files: [res.body.file],
        document: "Physician order",
        tab: "Fax",
        onSent: () =>
          void send(
            "POST",
            `/orders/${o.id}/sent`,
            {},
            "Sent to the physician",
            `The ${d.settings.orderDays}-day clock runs from when it was taken`,
          ),
      });
    };
    if (!d.orders.length) return el("p", { class: "muted small" }, "No orders yet.");
    return table(
      [
        {
          h: "Order",
          f: (o: Order) =>
            el(
              "span",
              {},
              el("b", { title: `From ${o.fromWhom} · taken ${o.taken}` }, o.what),
              el(
                "div",
                { class: "small muted" },
                `${o.kindLabel} · ${o.takenBy}${o.takenTitle ? `, ${o.takenTitle}` : ""}`,
              ),
            ),
        },
        { h: "Sent", f: (o) => sentWords(o) },
        { h: "Signed", f: (o) => signedChip(d, o) },
        {
          h: "",
          f: (o) =>
            el(
              "span",
              { class: "rowbtns" },
              o.file
                ? ib(
                    "eye",
                    "View the written order",
                    () =>
                      void openViewer({
                        file: o.file as { id: string; name: string },
                        document: "Physician order",
                      }),
                  )
                : null,
              o.signed?.file
                ? ib(
                    "check-circle",
                    "View the signed copy",
                    () =>
                      void openViewer({
                        file: o.signed?.file as { id: string; name: string },
                        document: "Physician order, signed",
                      }),
                  )
                : null,
              d.canEdit &&
                (o.cosign?.state === "waiting" || o.cosign?.state === "late") &&
                d.canCosign
                ? ib(
                    "check",
                    "Co-sign as RN",
                    () =>
                      void send(
                        "POST",
                        `/orders/${o.id}/cosign`,
                        {},
                        "Co-signed",
                        "The flag cleared",
                      ),
                  )
                : null,
              d.canEdit && !o.signed
                ? ib(
                    "paper-airplane",
                    "Send to the physician (fax or secure link)",
                    () => void sendIt(o),
                  )
                : null,
              d.canEdit && !o.signed
                ? ib("upload", "File the signed copy", () => signedModal(d, o.id))
                : null,
            ),
        },
      ],
      d.orders,
      undefined,
      "Physician orders",
    );
  }

  const line = (...parts: (Node | string | null)[]) =>
    el(
      "div",
      {
        style:
          "display:flex;justify-content:space-between;gap:8px;padding:6px 0;border-top:1px solid var(--line);flex-wrap:wrap",
      },
      ...parts,
    );

  function draw() {
    const d = data;
    if (!d || !d.shown) {
      host.replaceChildren();
      return;
    }
    const acts: Node[] = [];
    if (d.canEdit) {
      acts.push(ib("plus", "Add physician order", () => orderModal(d)));
      acts.push(ib("calendar", "Add appointment", appointmentModal));
    }
    host.replaceChildren(
      el(
        "section",
        { class: "card", "aria-label": "Next steps" },
        el(
          "div",
          { class: "cardhead" },
          el(
            "div",
            {},
            el("h2", {}, icon("checklist"), " Next steps"),
            el(
              "span",
              { class: "small muted" },
              "Orders, what's due and upcoming appointments for this episode.",
            ),
          ),
          el("span", { class: "actions" }, ...acts),
        ),
        el(
          "div",
          { class: "grid2", style: "gap:16px" },
          el(
            "div",
            { style: "min-width:0" },
            el("h3", { class: "h3s" }, "Physician orders"),
            filedHere("Filed as orders", d.filedOrders),
            ordersTable(d),
            el("h3", { class: "h3s" }, "Due next"),
            el(
              "div",
              { "aria-label": "Due next" },
              ...d.due.map((x) =>
                line(el("span", {}, el("b", {}, x.what)), chip(x.words, x.level)),
              ),
            ),
          ),
          el(
            "div",
            { style: "min-width:0" },
            el("h3", { class: "h3s" }, "Next appointments"),
            d.appointments.length
              ? el(
                  "div",
                  { "aria-label": "Next appointments" },
                  ...d.appointments.map((a) =>
                    el(
                      "div",
                      { style: "padding:8px 0;border-top:1px solid var(--line)" },
                      el("b", {}, a.withWhom),
                      a.when ? ` · ${a.when}` : "",
                      a.place || a.ride
                        ? el(
                            "div",
                            { class: "small muted" },
                            [a.place, a.ride].filter(Boolean).join(" · "),
                          )
                        : null,
                    ),
                  ),
                )
              : el("p", { class: "muted small" }, "No appointments coming up."),
          ),
        ),
      ),
    );
  }

  await reload();
  return { reload };
}
